'use client';
import InputString from '@/components/ui/InputString';
import {
  Briefcase,
  CircleDollarSignIcon,
  Clock4,
  MapPin,
  Search,
  UserRoundCheck,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import AdvancedSearch from '@/components/ui/AdvancedSearch';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useHomeSearch, useJobAutocomplete } from '@/features/jobs';
import HomeSearchMentorshipLink from '@/features/home/components/HomeSearchMentorshipLink';
import { trackStatistic } from '@/lib/utils/statisticTracker';

const HomeSearchNew = function () {
  const router = useRouter();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { data: autocompleteResults = [] } = useHomeSearch(searchQuery);

  useEffect(() => {
    if (searchQuery.trim() && autocompleteResults.length > 0) {
      setShowSearchResults(true);
    }
  }, [searchQuery, autocompleteResults]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleButtonClick = () => {
    if (searchQuery.trim()) {
      router.push(`/job/list?q=${searchQuery}`);
      setShowSearchResults(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      router.push(`/job/list?q=${(e.target as HTMLInputElement).value}`);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (result: string) => {
    setShowSearchResults(false);
    router.push(`/job/list?q=${result}`);
  };

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <div className="w-full md:max-w-[798px]">
        <div className="flex justify-center items-center gap-3 lg:gap-5 w-full p-4">
          <div className="relative w-full lg:w-[591px]" ref={searchRef}>
            <InputString
              name={'search'}
              placeholder="Та ямар ажил хайж байна вэ?"
              button={Search}
              className="h-11 w-full border-[1.5px] border-primary rounded-lg"
              onKeyDown={handleKeyDown}
              onButtonClick={handleButtonClick}
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => {
                if (searchQuery.trim() && autocompleteResults.length > 0) {
                  setShowSearchResults(true);
                }
              }}
              autoComplete="off"
            />

            {showSearchResults &&
              searchQuery.trim() &&
              autocompleteResults.length > 0 && (
                <div className="absolute left-0 z-[1000] top-[calc(100%+8px)] bg-white rounded-[12px] shadow-lg border border-gray-200 w-full max-h-[300px] overflow-auto">
                  {autocompleteResults.map((result, index) => (
                    <div
                      key={`srch-${index}`}
                      onClick={() => handleSearchResultClick(result)}
                      className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] sm:text-sm text-gray-900 truncate">
                          {result}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
          <Button
            className="hidden md:flex h-11 font-normal text-sm leading-[16px] px-8 tracking-normal whitespace-nowrap"
            variant="primary"
            onClick={() => {
              trackStatistic('advanced_filter');
              setShowModal(true);
            }}
          >
            <Image
              src={'/images/icons/filter-white.svg'}
              alt={'funnel'}
              width={44}
              height={44}
              className={`w-5 h-5`}
            />
            Дэлгэрэнгүй шүүлт
          </Button>

          <Button
            className={`md:hidden w-11 h-11 p-3`}
            variant="primary"
            onClick={() => setShowModal(true)}
          >
            <Image
              src={'/images/icons/filter-white.svg'}
              alt={'funnel'}
              width={44}
              height={44}
              className={`w-full h-full`}
            />
          </Button>
        </div>
        <AdvancedSearch
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          //categoryId={selectedCategoryId}
        />
      </div>
      <div className="flex flex-wrap justify-center items-center gap-2 px-4 pb-2 mt-2 text-sm md:gap-4 w-full">
        <Link
          href={`/job/near-me`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_near_me')}
        >
          <MapPin size="16" className="mr-2" /> <span>Надад ойр</span>
        </Link>
        <Link
          href={`/job/list?type=part_time`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_part_time')}
        >
          <Clock4 size="16" className="mr-2" /> <span>Цагийн ажил</span>
        </Link>
        <Link
          href={`/job/list?type=highSalary`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_high_salary')}
        >
          <CircleDollarSignIcon size="16" className="mr-2" />{' '}
          <span>Өндөр цалинтай</span>
        </Link>
        <Link
          href={`/job/list?type=english`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_english')}
        >
          <Briefcase size="16" className="mr-2" /> <span>Jobs in English</span>
        </Link>
        <Link
          href={`/job/list?type=no-experience`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_no_experience')}
        >
          <UserRoundCheck size="16" className="mr-2" />{' '}
          <span>Туршлага хамаарахгүй</span>
        </Link>
        <Link
          href={`/job/list?type=cv`}
          className="flex items-center p-2 bg-[#f7f9fb] text-[#555] hover:text-black rounded-lg hover:bg-gray-50 hover:shadow-md"
          style={{ boxShadow: '2px 2px 4px #e1e4e7' }}
          onClick={() => trackStatistic('quick_filter_most_cv')}
        >
          <span>🔥 Хамгийн ...</span>
        </Link>
        <HomeSearchMentorshipLink />
      </div>
      <AdvancedSearch
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        //categoryId={selectedCategoryId}
      />
    </div>
  );
};

export default HomeSearchNew;
